import { ref, watch } from 'vue';

/**
 * Singleton guided tour store (composable).
 *
 * - Persisted per user (localStorage): `started`, `completed`, `index`
 * - In-memory only (shared across consumers): `active`, `currentStepId`
 *
 * Persisted localStorage shape (per user):
 * { started: boolean, completed: boolean, index: number }
 */
const userId = ref(null);

const started = ref(false);
const completed = ref(false);
const index = ref(0);

const active = ref(false);

const currentStepId = ref(null);

const keyPrefix = 'en_guided_tour_state_v1';
let initialized = false;

function getStorageKey() {
    return `${keyPrefix}_${userId.value}`;
}

function sanitize(payload) {
    if (!payload || typeof payload !== 'object') {
        return {started: false, completed: false, index: 0};
    }

    return {
        started: !!payload.started,
        completed: !!payload.completed,
        index: Number.isFinite(payload.index) ? payload.index : 0,
    };
}

function loadPersisted() {
    try {
        const raw = localStorage.getItem(getStorageKey());
        if (!raw) return null;
        return sanitize(JSON.parse(raw));
    } catch {
        return null;
    }
}

function savePersisted() {
    localStorage.setItem(
        getStorageKey(),
        JSON.stringify({
            started: started.value,
            completed: completed.value,
            index: index.value,
        })
    );
}

function ensureInitialized() {
    if (initialized) return;
    initialized = true;

    const saved = loadPersisted();
    if (saved) {
        started.value = saved.started;
        completed.value = saved.completed;
        index.value = saved.index;
    }

    watch([started, completed, index], savePersisted);
}

export function useGuidedTour(newUserId) {
    userId.value = newUserId;
    ensureInitialized();

    function setProgress({started: s, completed: c, index: i} = {}) {
        if (typeof s !== 'undefined') started.value = !!s;
        if (typeof c !== 'undefined') completed.value = !!c;
        if (typeof i !== 'undefined') index.value = Number.isFinite(i) ? i : 0;
    }

    function setCurrentStepId(stepId) {
        currentStepId.value = stepId ?? null;
    }

    function reset() {
        started.value = false;
        completed.value = false;
        index.value = 0;
        currentStepId.value = null;
    }

    return {
        started,
        completed,
        index,
        active,
        currentStepId,

        setProgress,
        setCurrentStepId,
        reset,

        load: loadPersisted,
        save: savePersisted,
    };
}
